import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { prefersDip } from '../../anglePreference';
import { GyroRecordChart } from './charts/GyroRecordChart';
import { OrientationRecordChart } from './charts/OrientationRecordChart';
import { GammaRecordChart } from './charts/GammaRecordChart';
import { SurveyPlanChart, SurveySectionChart } from './charts/SurveyTraceChart';
import { ShotSourceModal } from './ShotSourceModal';
import { AzimuthDatumBar } from './AzimuthDatumBar';
import { DepthRunPanel } from './DepthRunPanel';
import { hasDepthRun, coverageByShot, MAX_SHOT_RESIDUAL_METRES } from '../../depthTrace';
import { assessSurvey, hasPlottableRecords, isGammaSurvey, isHddSurvey } from '../../surveyCompleteness';
import { shotQaPass, surveyQaBands } from '../../surveyQa';
import { isUserEdited, editedBy } from '../../shotProvenance';
import { initialApproval } from '../../approvalSeed';

export const SurveyPreviewTable = ({ selectedSurvey, surveyKey, industry, angleDisplay, WTotal, WTolerance, GTotal, approvedLinesChanged, gridCorrection, onSurveyUpdated }) => {
    const { t } = useTranslation();
    const showDip = prefersDip(angleDisplay, industry);
    // Shown only when a correction actually applies to this survey, the
    // same condition as the banner on the approval page.
    const showCorrected = !!gridCorrection;
    // Wrapped as the server does, so the screen and the exported file agree
    // about an azimuth near north even under a negative correction.
    const correctedAzimuth = (azimuth) => {
        const wrapped = (azimuth + (gridCorrection?.correction || 0)) % 360;
        return (wrapped < 0 ? wrapped + 360 : wrapped).toFixed(2);
    };
    const [loading, setLoading] = useState(true);
    const [survey, setSurvey] = useState(selectedSurvey);
    const [checkedLines, setCheckedLines] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [resourcingShotId, setResourcingShotId] = useState(null);
    const [positions, setPositions] = useState(null);

    // Single switch for continuous (FastTrak) surveys: drives the QA skip, the
    // hidden GTotal/WTotal columns, and which gyro chart to render. FastTrak gyro
    // records have no real WTotal (it's auto-populated with a placeholder), so the
    // latitude-derived Earth-rate QA check doesn't apply.
    const isFastTrak = !!(selectedSurvey && selectedSurvey.DeviceName && selectedSurvey.DeviceName.startsWith('FastTrak'));

    // Gamma loggers produce a counts log and no shots at all, so they take the
    // no-shots render path below but still have something worth showing.
    const isGamma = isGammaSurvey(selectedSurvey);

    // HDD is a realtime service: shots are merged live rather than after a
    // download, so the survey has no GyroRecords or DepthTimeRecords by design.
    const isHdd = isHddSurvey(selectedSurvey);

    // A "Run QA" column, only for a survey that was actually run on a depth
    // counter. It repeats per shot what the panel at the foot of the page says
    // for the run as a whole, so an approver gets the answer without scrolling
    // to the chart. It is read-only and feeds nothing: the approve ticks are
    // decided by sensor QA alone, exactly as they were.
    // Both read `survey` rather than `selectedSurvey`: the rows rendered below come
    // from `survey`, and the map is keyed on those shot objects.
    const showRunQa = hasDepthRun(survey);
    const runQaByShot = showRunQa ? coverageByShot(survey) : null;

    // Everything QA needs for this tool class: the expected Earth rate and its
    // window, the gravity window, and whether the tool has a bore-axis gyro at
    // all. A tool without one reconstructs its azimuth from gravity and latitude,
    // which is undefined near horizontal, so QA carries an inclination band those
    // shots have to sit outside of - MiNS Memory always, AQ from serial 4001 on.
    const bands = surveyQaBands(selectedSurvey);

    // Admins can re-source a shot from a neighbouring gyro record - or, on an OSA
    // Gyro, a neighbouring wake, which the server and the modal handle as their own
    // unit. This needs the raw records and the calibration to redo the orientation
    // maths: FastTrak is continuous and ships pre-calculated orientations, and an
    // HDD shot is a discrete gyrocompass operation with no surrounding log to pick
    // from.
    const canResource = !!(isAdmin && !isFastTrak && !isHdd && surveyKey
        && survey.GyroRecords && survey.GyroRecords.length > 0
        && survey.CalibrationFile);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch('api/identity/getuser');
                if (response.ok) {
                    const data = await response.json();
                    setIsAdmin(!!(data.roles && data.roles.some(r => r.toLowerCase() === 'admin')));
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };
        fetchUser();
    }, []);


    // The survey this component last derived its tick state from. An in-place
    // edit — re-sourcing a shot, changing the azimuth datum — hands back a fresh
    // survey object for the *same* survey, and the operator's approval decisions
    // have to survive that. Only a genuinely different survey re-derives them.
    const initialisedKey = useRef(null);

    useEffect(() => {
        if (selectedSurvey) {
            setLoading(false);
            const shots = selectedSurvey.SavedShots;
            const isNewSurvey = initialisedKey.current !== surveyKey;

            if (isNewSurvey) {
                // Drop the previous survey's trace immediately. The replacement is
                // debounced, so leaving it up would show one survey's hole under
                // another's shot table for a moment. On an in-place edit it is the
                // same hole, so clearing would only make the charts blink.
                setPositions(null);
            }

            // Initialize checkedLines from QA, or from the earlier decision on a
            // survey returned from its drillhole. Shots are rewritten in place by
            // every edit path, so an unchanged count means the indices the ticks
            // are keyed on are still the same shots.
            if (shots && (isNewSurvey || checkedLines.length !== shots.length)) {
                const derived = initialApproval(shots, qaCheckBool);
                setCheckedLines(derived);
                if (approvedLinesChanged) {
                    const initialApproved = derived
                        .map((checked, idx) => checked ? idx : null)
                        .filter(idx => idx !== null);
                    approvedLinesChanged(initialApproved);
                }
            }
            setSurvey(selectedSurvey);
            initialisedKey.current = surveyKey;
        }
    }, [selectedSurvey, surveyKey]); // eslint-disable-line react-hooks/exhaustive-deps -- qaCheckBool reads stable props (GTotal/WTotal) and isFastTrak (derived from selectedSurvey); approvedLinesChanged is a prop callback; checkedLines is read only to detect a shot-count change


    function qaCheck(value) {
        return qaCheckBool(value)
            ? <span style={{ color: 'green' }}>✔</span>
            : <span style={{ color: 'red' }}>✘</span>;
    }

    // The same tick and cross as the sensor QA column, but about the depth
    // counter: does this shot's depth sit on the trace at its own timestamp?
    //
    // Blank where the shot could not be compared, which is not a third verdict
    // dressed up as a gap — there is genuinely nothing to report. A counter
    // connected part-way through a run leaves the earlier shots with no trace to
    // lie on, and marking those either way would be an invention.
    function runQaCheck(row) {
        const mark = runQaByShot?.get(row);
        if (!mark || mark.status !== 'covered') return null;
        const pass = mark.residual <= MAX_SHOT_RESIDUAL_METRES;
        return (
            <span
                style={{ color: pass ? 'green' : 'red' }}
                title={t('approval.runQaCellTitle', { counter: mark.interpolated.toFixed(2), residual: mark.residual.toFixed(2) })}>
                {pass ? '✔' : '✘'}
            </span>
        );
    }

    // Derived here as a fallback so the table stays right if a caller passes
    // WTotal without the matching tolerance.
    const wTolerance = WTolerance !== undefined ? WTolerance : bands.wTolerance;

    // How the re-source endpoint addresses a shot. MiNS Memory surveys are
    // written by GTLCore's project serialiser, which had no Id on SavedShot until
    // Navigator v31, so every MiNS survey already in a repository has none and
    // matching on Id alone 404s the whole tool. The positional form is tagged so
    // the server can never mistake it for a GUID, and it is only ever held for the
    // life of one modal.
    function shotId(row, index) {
        return row.Id || `index:${index}`;
    }

    function qaCheckBool(value) {
        if (isFastTrak) return true;
        return shotQaPass(value.OrientationSet, GTotal, WTotal, wTolerance, bands.noBoreGyro, bands.gTolerance);
    }

    // Checkbox change handler
    const handleCheckboxChange = (index) => {
        setCheckedLines(prev => {
            const updated = [...prev];
            updated[index] = !updated[index];
            return updated;
        });
        if (approvedLinesChanged) {
            const updatedIndexes = checkedLines
                .map((checked, idx) => checked ? idx : null)
                .filter(idx => idx !== null);
            if (!checkedLines[index]) {
                updatedIndexes.push(index);
            } else {
                const removeIndex = updatedIndexes.indexOf(index);
                if (removeIndex > -1) {
                    updatedIndexes.splice(removeIndex, 1);
                }
            }
            approvedLinesChanged(updatedIndexes);
        }
    };

    // Minimum-curvature positions for the current tick state. Recomputed
    // server-side because the maths lives there (see Services/SurveyPositions.cs)
    // and because excluding a shot moves every station below it — the plan view
    // would otherwise show a trajectory that no longer matches the selection.
    // Debounced so dragging through the select-all checkbox doesn't fire a
    // request per row.
    useEffect(() => {
        const shots = survey.SavedShots || [];
        if (!surveyKey || shots.length === 0 || checkedLines.length !== shots.length) {
            return undefined;
        }
        const approvedLines = checkedLines
            .map((checked, idx) => (checked ? idx : null))
            .filter(idx => idx !== null);
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`api/surveys/${surveyKey}/positions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ approvedLines }),
                    signal: controller.signal
                });
                if (response.ok) {
                    setPositions(await response.json());
                }
            } catch (error) {
                // Aborted by a newer change, or the request failed. Either way
                // leave the last good trace on screen rather than blanking it.
            }
        }, 300);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [surveyKey, survey.SavedShots, checkedLines]);

    // Render order: depth-ascending for multishot surveys. The saving app does
    // insert(0) so the newest shot is at the top, which is neither depth nor a
    // useful display order. Continuous FastTrak surveys are round-trip chronological
    // (depth goes positive then negative), so they stay in saved order. All checkbox
    // and approval state is keyed on the original SavedShots index, so sorting only
    // affects render order — approvedLines and the CSV export still line up.
    const displayOrder = React.useMemo(() => {
        const shots = survey.SavedShots || [];
        const indexes = shots.map((_, idx) => idx);
        if (isFastTrak) return indexes;
        return indexes.sort((a, b) => shots[a].Depth - shots[b].Depth);
    }, [survey.SavedShots, isFastTrak]);

    // Index sets the plan view styles by: which stations were dropped, and which
    // were kept in spite of failing QA.
    const rejectedIndexes = React.useMemo(() => new Set(
        (survey.SavedShots || []).map((_, idx) => idx).filter(idx => !checkedLines[idx])
    ), [survey.SavedShots, checkedLines]);

    const qaFailIndexes = React.useMemo(() => new Set(
        (survey.SavedShots || []).map((row, idx) => (qaCheckBool(row) ? null : idx)).filter(idx => idx !== null)
    ), [survey.SavedShots]); // eslint-disable-line react-hooks/exhaustive-deps -- qaCheckBool reads stable props (GTotal/WTotal) and isFastTrak

    const allChecked = survey.SavedShots && survey.SavedShots.length > 0
        && checkedLines.length === survey.SavedShots.length
        && checkedLines.every(Boolean);

    const handleToggleAll = () => {
        if (!survey.SavedShots) return;
        const newState = !allChecked;
        const updated = survey.SavedShots.map(() => newState);
        setCheckedLines(updated);
        if (approvedLinesChanged) {
            approvedLinesChanged(newState ? survey.SavedShots.map((_, idx) => idx) : []);
        }
    };

    // An uploaded survey is not necessarily a finished one — a run can be
    // uploaded before the tool has been downloaded. Say so explicitly rather
    // than rendering an empty table and an empty chart.
    const completeness = assessSurvey(survey);

    if (!completeness.hasSurvey) {
        return <p>{t('approval.noSurveySelected')}</p>;
    }

    const warnings = completeness.isIncomplete ? (
        <div className={`alert alert-${completeness.issues.some(i => i.level === 'danger') ? 'danger' : 'warning'}`} role="alert">
            <h5 className="alert-heading">{completeness.headline || t('approval.looksIncomplete')}</h5>
            <ul className="mb-0">
                {completeness.issues.map((issue, idx) => <li key={idx}>{issue.text}</li>)}
            </ul>
        </div>
    ) : null;

    // Which record stream this tool plots, and the placeholder to show when it
    // arrived empty. Built once because the no-shots branch below needs it too:
    // a gamma survey has no shot table but its log is the whole point of the page.
    const sensorKey = isGamma ? 'gamma' : isFastTrak ? 'orientation' : 'gyro';
    const recordChart = isHdd
        // HDD is merged live and never carries a raw record log, so there is no
        // missing data to apologise for — just don't offer the section.
        ? null
        : !hasPlottableRecords(survey)
        ? <p className="text-muted fst-italic">
            {t('approval.noRecordsToChart', { sensor: t(`completeness.sensor.${sensorKey}`) })}
        </p>
        : isGamma
            ? <GammaRecordChart gammaRecords={survey.GammaRecords} depthRecords={survey.DepthTimeRecords} height={400} />
            : isFastTrak
                ? <OrientationRecordChart orientationRecords={survey.OrientationRecords} depthRecords={survey.DepthTimeRecords} height={400} />
                : <GyroRecordChart gyroRecords={survey.GyroRecords} depthRecords={survey.DepthTimeRecords} height={400} />;

    return survey.SavedShots && survey.SavedShots.length > 0 ? (

        <div>
            {/*<button className="btn btn-primary" onClick={handleSurveyDownload}>Download Survey</button>*/}
            {warnings}
            {!isFastTrak && <h5>WTotal: {WTotal.toFixed(2)} &plusmn; {wTolerance.toFixed(2)}</h5>}
            {/* Settled here, before approval, rather than only afterwards from the
                drillhole view: the plan/section preview below re-solves on the new
                azimuths, so the datum is decided against the trace it produces.
                Renders nothing for a multishot survey, where WTotal takes this row. */}
            <AzimuthDatumBar survey={survey} surveyKey={surveyKey}
                canEdit={isAdmin} onSurveyUpdated={onSurveyUpdated} />
            {survey.SavedShots && (
                <div>
                    <table className="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>{t('survey.depth')}</th>
                                {/* The same rule as anglePreference.angleHeader, whose English
                                    constants are also compared against the stored setting. */}
                                <th>{t(showDip ? 'survey.dip' : 'survey.inclination')}</th>
                                <th>{t('survey.azimuthTrueNorth')}</th>
                                {showCorrected && <th>{t('survey.azimuthCorrected')}</th>}
                                {!isFastTrak && <th>GTotal</th>}
                                {!isFastTrak && <th>WTotal</th>}
                                <th>QA</th>
                                {showRunQa && <th title={t('approval.runQaTitle', { threshold: MAX_SHOT_RESIDUAL_METRES.toFixed(1) })}>{t('approval.runQa')}</th>}
                                {canResource && <th>{t('approval.source')}</th>}
                                <th>
                                    {t('approval.approve')}
                                    <input
                                        type="checkbox"
                                        className="ms-2"
                                        title={allChecked ? t('approval.deselectAll') : t('approval.selectAll')}
                                        checked={allChecked}
                                        onChange={handleToggleAll}
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan="8">{t('common.loading')}</td></tr> : displayOrder.map((index) => {
                                const row = survey.SavedShots[index];
                                return (
                                    <tr key={index}>
                                        <td>{row.Depth.toFixed(2)}</td>
                                        <td>{(showDip ? row.OrientationSet.Inclination - 90 : row.OrientationSet.Inclination).toFixed(2)}</td>
                                        <td>{row.OrientationSet.Azimuth.toFixed(2)}</td>
                                        {showCorrected &&
                                            <td>{correctedAzimuth(row.OrientationSet.Azimuth)}</td>
                                        }
                                        {!isFastTrak && <td>{row.OrientationSet.GTotal.toFixed(3)}</td>}
                                        {!isFastTrak && <td>{row.OrientationSet.WTotal.toFixed(2)}</td>}
                                        <td>{qaCheck(row)}</td>
                                        {showRunQa && <td>{runQaCheck(row)}</td>}
                                        {canResource && (
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${qaCheckBool(row) ? 'btn-link text-muted' : 'btn-outline-danger'}`}
                                                    title={t('approval.resourceTitle')}
                                                    onClick={() => setResourcingShotId(shotId(row, index))}>
                                                    {t('approval.resource')}
                                                </button>
                                                {/* Keyed on who, not on where. The tool writes
                                                    SourceGyroIndex on every shot from app v2.4 —
                                                    it selects its own record — so that field
                                                    marks an optimised survey, not an edited one.
                                                    See shotProvenance.js. */}
                                                {isUserEdited(row) && (
                                                    <span className="badge bg-info ms-1"
                                                        title={editedBy(row)
                                                            ? t('approval.resourcedToBy', { index: row.SourceGyroIndex, name: editedBy(row) })
                                                            : t('approval.resourcedTo', { index: row.SourceGyroIndex })}>
                                                        {editedBy(row) ? t('approval.editedBy', { name: editedBy(row) }) : t('approval.edited')}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={checkedLines[index] || false}
                                                onChange={() => handleCheckboxChange(index)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {positions && (
                        // Both views stay square, so they sit side by side on
                        // wide screens and stack below lg.
                        <div className="row">
                            <div className="col-lg-6">
                                <SurveyPlanChart
                                    approved={positions.Approved}
                                    all={positions.All}
                                    rejectedIndexes={rejectedIndexes}
                                    qaFailIndexes={qaFailIndexes}
                                />
                            </div>
                            <div className="col-lg-6">
                                <SurveySectionChart
                                    approved={positions.Approved}
                                    all={positions.All}
                                    rejectedIndexes={rejectedIndexes}
                                    qaFailIndexes={qaFailIndexes}
                                />
                            </div>
                        </div>
                    )}
                    {recordChart}
                    {/* Last, below the shots and the trajectory. It is supporting
                        evidence about the run rather than something the approver
                        decides on, so it reads after the survey itself rather than
                        ahead of it. Renders nothing for a survey with no depth
                        counter, which is most of them — so it must not be wrapped. */}
                    <DepthRunPanel survey={survey} height={260} />
                </div>
            )}
            {canResource && (
                <ShotSourceModal
                    isOpen={!!resourcingShotId}
                    surveyKey={surveyKey}
                    shotId={resourcingShotId}
                    WTolerance={wTolerance}
                    onClose={() => setResourcingShotId(null)}
                    onSurveyUpdated={onSurveyUpdated}
                />
            )}
        </div>
    ) : isGamma ? (
        // A gamma logger has no shots to tick, so there is no table and no
        // trajectory — the counts log is what there is to look at before
        // allocating the run to a drillhole.
        <div>
            {warnings}
            <h5>{t('approval.gammaRecordCount')} {(survey.GammaRecords || []).length}</h5>
            {recordChart}
            {/* A gamma logger can be run on a counter too; it has depth marks and
                no shots, and the panel cross-checks those instead. */}
            <DepthRunPanel survey={survey} height={260} />
        </div>
    ) : (
        // A survey is loaded but has nothing to approve. Previously this fell
        // through to "No survey selected", which read as though the click had
        // not registered.
        <div>
            {warnings || (
                <div className="alert alert-warning" role="alert">
                    {t('approval.noShotsToApprove')}
                </div>
            )}
        </div>
    );
}
